import { LocaleMessageObject } from "vue-i18n";
import Starling from "@ies/starling_client";
import { getRealLang, getRevertLang, Lang } from '@/const/locale'
import {
  DEFAULT_LANG,
  STARLING_COMMON_CONFIG,
  STARLING_NAMESPACES,
  EXTENDS_NAMESPACES,
  EXTENDS_STARLING_KEY_MAP
} from "../const/starling";
// 已经加载的 starling 空间
const loadedNamesSpace: { [key: string]: boolean } = {};
interface ILangMsg {
  lang: string;
  text: LocaleMessageObject | null;
}

type IStringArr = string[];
type ILangMsgPromiseArr = Promise<ILangMsg>[];
/**
 * @param  {IStringArr} namespaces
 * @param  {IStringArr} languages
 * @param  {boolean} isExtends?
 * 获取load starling Promise数组
 */
function getStarlingPromises(
  namespaces: IStringArr,
  languages: IStringArr,
  isExtends?: boolean
): ILangMsgPromiseArr {
  const PromiseArr: ILangMsgPromiseArr = [];
  languages.forEach((lang: string) => {
    namespaces.forEach(namespace => {
      if (!loadedNamesSpace[`${namespace}-${lang}`]) {
        PromiseArr.push(
          new Promise((resolve, reject) => {
            const starling = new Starling({
              ...STARLING_COMMON_CONFIG,
              api_key:
                (isExtends === true && EXTENDS_STARLING_KEY_MAP[namespace]) ||
                STARLING_COMMON_CONFIG.api_key,
              namespace,
              locale: lang,
              timeout: 30000
            });
            try {
              starling.load((text: LocaleMessageObject) => {
                resolve({ lang, text } || {});
                loadedNamesSpace[`${namespace}-${lang}`] = true;
              });
            } catch (err) {
              reject(new Error("请求失败"));
            }
          })
        );
      }
    });
  });
  return PromiseArr;
}
/**
 * @param  {any} route
 * @returns { curNameSpaces, extendsNamespaces }
 * 通过route 获取命名空间数组
 */
function getNameSpaces(route: any) {
  // 获取当前的路由匹配的namespace
  let curRoute = route.name || "";
  const paramKeys: string[] = Object.keys(route.params);
  // 这一步是去掉 -region 这样的参数
  paramKeys.forEach(item => {
    curRoute = curRoute.replace("-" + item, "");
  });
  // 获取当前的路由匹配的namespace
  const curNameSpaces = STARLING_NAMESPACES[curRoute]
    ? Array.from(
        new Set((STARLING_NAMESPACES[curRoute] || []).concat(["common"]))
      )
    : [];
  // 获取当前的路由匹配的其他项目的扩展starling 空间
  const extendsNamespaces: IStringArr = EXTENDS_NAMESPACES[curRoute] || [];
  return {
    curNameSpaces,
    extendsNamespaces
  };
}

function getStarlingLang(lang: string, isExtends = false) {
  if (isExtends && lang.startsWith(Lang.English))  {
    return Lang.English
  }
  return getRealLang(lang)
}

/**
 * @param  {IStringArr} namespaces
 * @param  {IStringArr} languages
 * @param  {boolean} isExtends?
 * 获取本地文案 Promise
 */
function getImportPromises(
  namespaces: IStringArr,
  languages: IStringArr,
  isExtends?: boolean
): ILangMsgPromiseArr {
  return languages.reduce(
    (proArr: ILangMsgPromiseArr, lang) => [
      ...proArr,
      ...namespaces.map(name => {
        const importLang = getStarlingLang(lang, isExtends)
        const folderName = isExtends ? "languages_extends" : "languages";
        return new Promise((resolve) =>
          import(`../const/${folderName}/${name}/${importLang}.json`)
            .then(module => resolve({ lang, text: module.default }))
            .catch(() => resolve({ lang, text: {} }))
        ) as Promise<ILangMsg>;
      })
    ],
    []
  );
}

/**
 * @param  ILangMsgPromiseArr localePromises
 * 通过 Promises 数组获取 i18n messages
 */
function getMsgByPromises(localePromises: ILangMsgPromiseArr) {
  return Promise.all(localePromises).then(data =>
    data.reduce((total: { [lang: string]: LocaleMessageObject }, item) => {
      const revertLang = getRevertLang(item.lang)
      item.text && (total[revertLang] = { ...total[revertLang], ...item.text });
      return total;
    }, {})
  );
}
/**
 * @param  {any} route
 * @param  {string} lang
 * @param  {boolean} fromLocal
 * 通用的获取文案方法
 */
function getLocaleMsg(route: any, lang: string, fromLocal: boolean) {
  const languages =
    getRealLang(lang) === DEFAULT_LANG ? [DEFAULT_LANG] : [DEFAULT_LANG, getRealLang(lang)];
  const { curNameSpaces, extendsNamespaces } = getNameSpaces(route);
  const getPromiseFn = fromLocal ? getImportPromises : getStarlingPromises;
  const localePromises = [
    ...getPromiseFn(curNameSpaces, languages),
    ...getPromiseFn(extendsNamespaces, languages, true)
  ];
  return getMsgByPromises(localePromises);
}

export function getLangDataFromLocal(route: any, lang: string) {
  return getLocaleMsg(route, lang, true);
}

export function getLangData(route: any, lang: string) {
  return getLocaleMsg(route, lang, false);
}
